import LoginInertiaController from '@/actions/App/Http/Controllers/User/Auth/LoginInertiaController';
import ProfileInertiaController from '@/actions/App/Http/Controllers/User/ProfileInertiaController';
import UserInertiaController from '@/actions/App/Http/Controllers/User/UserInertiaController';
import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { HambergerMenu } from '@/components/icons/HambergerMenu';
import { Link } from '@inertiajs/react';
import { ArrowUpRight, LogOut, MapPin, Search, ShoppingCart, Ticket, User, User2 } from 'lucide-react';
import { type NavItem, type NavLink } from './use-nav-items';

interface NavMobileDrawerProps {
  navItems: NavItem[];
  cartCount: number;
  isAuthenticated: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onLogout: () => void;
}

/** Every tap target sits on this height so the drawer clears the 44px WCAG floor. */
const ROW = 'flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors';

export function NavMobileDrawer({ navItems, cartCount, isAuthenticated, isOpen, onOpenChange, onLogout }: NavMobileDrawerProps) {
  const close = () => onOpenChange(false);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <HambergerMenu isOpen={isOpen} />
      </SheetTrigger>

      <SheetContent side="left" className="z-[51] flex w-[65vw] max-w-sm flex-col gap-0 overflow-y-auto p-0">
        <SheetHeader className="px-5 pt-6 pb-4">
          <SheetTitle className="text-left text-base">Menu</SheetTitle>
        </SheetHeader>

        {/* The primary CTA used to live inside the desktop-only nav, so mobile had no way
            to reach booking at all. It leads here. */}
        <div className="px-5 pb-4">
          <Button variant="destructive" className="h-11 w-full rounded-full" asChild>
            <Link href={SiteInertiaController.search().url} onClick={close}>
              <Ticket className="size-4" />
              Đặt vé
            </Link>
          </Button>
        </div>

        <nav className="flex-1 px-5 pb-6">
          <Accordion type="multiple" className="w-full">
            {navItems.map((item) =>
              item.groups ? (
                <AccordionItem key={item.title} value={item.title} className="border-gray-100">
                  <AccordionTrigger className="min-h-11 text-sm font-semibold text-gray-900 hover:no-underline">{item.title}</AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-0 pb-3">
                    {item.groups.map((group) => (
                      <div key={group.location} className="space-y-1">
                        <p className="text-primary px-3 text-[11px] font-bold tracking-[0.12em] uppercase">{group.location}</p>
                        {group.links.map((link) => (
                          <MobileNavLink key={link.key} link={link} onNavigate={close} />
                        ))}
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ) : (
                <div key={item.title} className="border-b border-gray-100">
                  <Link href={item.href || '#'} className="hover:text-primary flex min-h-11 items-center py-4 text-sm font-semibold text-gray-900" onClick={close}>
                    {item.title}
                  </Link>
                </div>
              ),
            )}
          </Accordion>

          <div className="mt-6 space-y-1 border-t border-gray-100 pt-4">
            <Link href={SiteInertiaController.search().url} className={`${ROW} text-gray-700 hover:bg-[var(--surface-muted)]`} onClick={close}>
              <Search className="size-4" />
              Tìm kiếm
            </Link>
            <Link href="/cart" className={`${ROW} text-gray-700 hover:bg-[var(--surface-muted)]`} onClick={close}>
              <ShoppingCart className="size-4" />
              Giỏ hàng{cartCount > 0 ? ` (${cartCount})` : ''}
            </Link>
          </div>

          <div className="mt-4 space-y-1 border-t border-gray-100 pt-4">
            {isAuthenticated ? (
              <>
                <Link href={UserInertiaController.home().url} className={`${ROW} text-gray-700 hover:bg-[var(--surface-muted)]`} onClick={close}>
                  <Ticket className="size-4" />
                  Vé của tôi
                </Link>
                <Link href={ProfileInertiaController.profile().url} className={`${ROW} text-gray-700 hover:bg-[var(--surface-muted)]`} onClick={close}>
                  <User2 className="size-4" />
                  Hồ sơ
                </Link>
                <Link
                  href={LoginInertiaController.logout().url}
                  className={`${ROW} text-primary hover:bg-primary/5`}
                  onClick={(event) => {
                    event.preventDefault();
                    close();
                    onLogout();
                  }}
                >
                  <LogOut className="size-4" />
                  Đăng xuất
                </Link>
              </>
            ) : (
              <Link href="/user/login" className={`${ROW} text-gray-700 hover:bg-[var(--surface-muted)]`} onClick={close}>
                <User className="size-4" />
                Đăng nhập
              </Link>
            )}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

function MobileNavLink({ link, onNavigate }: { link: NavLink; onNavigate: () => void }) {
  const content = (
    <>
      <span className="size-10 shrink-0 overflow-hidden rounded-lg bg-[var(--surface-muted)]">
        {link.image ? (
          <img src={link.image} alt="" loading="lazy" decoding="async" className="size-full object-cover" />
        ) : (
          <span className="flex size-full items-center justify-center text-gray-400">
            <MapPin className="size-4" aria-hidden="true" />
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1 truncate text-sm font-medium text-gray-900">
          {link.title}
          {link.isExternal && <ArrowUpRight className="size-3 shrink-0 text-gray-400" aria-hidden="true" />}
        </span>
        {link.description && <span className="mt-0.5 line-clamp-1 block text-xs text-gray-500">{link.description}</span>}
      </span>
    </>
  );

  const className = `${ROW} py-2 font-normal hover:bg-[var(--surface-muted)]`;

  if (link.isExternal) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onNavigate}>
        {content}
      </a>
    );
  }

  return (
    <Link href={link.href} className={className} onClick={onNavigate}>
      {content}
    </Link>
  );
}
